import React, { useState } from 'react'
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material'
import type { ThemeAIScreening } from '../../types/gotypes'
import AiAccessTooltip from '../system/AiAccessTooltip'
import DisabledReason from '../system/DisabledReason'
import JunkCheckConfirmDialog, { JunkCheckConfirmDetails } from './JunkCheckConfirmDialog'
import { effectiveScreening, junkAppliedLine, junkCountLabel, junkDecisionWords, junkFlaggedCount } from '../../utils/themeAIJunk'
import { personalDataOfferLine } from '../../utils/themeAIPersonalData'

export interface JunkCheckOfferProps {
  /** The check on the current data, if one has been made. */
  screening?: ThemeAIScreening
  estimate?: JunkCheckConfirmDetails
  estimateLoading: boolean
  /** A check is being started or cancelled. */
  starting: boolean
  /** Why starting a check is closed to this person (AI access, or not their task), or undefined. */
  blockedReason?: string
  /** Why cancelling or changing a check is closed (the person cannot change the task), or undefined. */
  writeBlockedReason?: string
  /** The confirmation opened or closed. The page reads the estimate only while it is open. */
  onConfirmOpenChange: (open: boolean) => void
  /** Proceed in the confirmation: starts the check. */
  onStartCheck: () => Promise<unknown>
  onReview: () => void
  /** Opens the personal data review (#494), the same check's second finding. */
  onReviewPersonalData?: () => void
  /** Cancels a check that is running. */
  onDiscard: () => Promise<unknown> | void
}

// Run's other question, beside "Do you want to test this before running?":
// check for answers that are not answers before paying to code them. Always
// optional, never on data load, since it spends.
const JunkCheckOffer: React.FC<JunkCheckOfferProps> = ({
  screening,
  estimate,
  estimateLoading,
  starting,
  blockedReason,
  writeBlockedReason,
  onConfirmOpenChange,
  onStartCheck,
  onReview,
  onReviewPersonalData,
  onDiscard,
}) => {
  const [confirmOpen, setConfirmOpenState] = useState(false)
  const setConfirmOpen = (open: boolean) => {
    setConfirmOpenState(open)
    onConfirmOpenChange(open)
  }

  const handleProceed = async () => {
    try {
      await onStartCheck()
    } catch {
      // The hook reports it.
    } finally {
      setConfirmOpen(false)
    }
  }

  const handleCancel = async () => {
    try {
      await onDiscard()
    } catch {
      // The hook reports it.
    }
  }

  const dialog = (
    <JunkCheckConfirmDialog
      open={confirmOpen}
      loading={estimateLoading}
      starting={starting}
      details={estimate}
      onCancel={() => !starting && setConfirmOpen(false)}
      onProceed={handleProceed}
    />
  )

  if (screening && (screening.status === 'pending' || screening.status === 'running')) {
    const total = screening.requests_total ?? 0
    const done = screening.requests_done ?? 0
    const known = total > 0
    return (
      <Box sx={{ maxWidth: 560 }} aria-live="polite">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 1 }}>
          <Typography variant="body2">Checking {screening.response_count.toLocaleString()} answers for junk…</Typography>
          <DisabledReason reason={writeBlockedReason}>
            <Button size="small" color="info" onClick={handleCancel} disabled={starting}>Cancel</Button>
          </DisabledReason>
        </Box>
        <LinearProgress
          color="info"
          variant={known ? 'determinate' : 'indeterminate'}
          value={known ? Math.min(100, Math.round((done / total) * 100)) : 0}
          aria-label={known ? `${done} of ${total} batches checked` : 'Checking answers'}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {known ? `${done.toLocaleString()} of ${total.toLocaleString()} batches · ` : ''}You can leave this page. Review and run waits until the check finishes.
        </Typography>
        {screening.applied && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {junkAppliedLine(screening.applied)}
          </Typography>
        )}
      </Box>
    )
  }

  // The check runs use: the latest if it completed, else the older one the
  // server keeps applying while a newer check failed or was cancelled.
  const inUse = effectiveScreening(screening)
  if (inUse) {
    const flagged = junkFlaggedCount(inUse)
    const newerFailed = screening?.status === 'failed'
    const personalData = (inUse.personal_data_count ?? 0) > 0
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {newerFailed
              ? junkAppliedLine(inUse)
              : flagged === 0 ? 'No junk answers found.' : `${junkCountLabel(flagged)} found · ${junkDecisionWords(inUse.decision)}.`}
            {inUse.unchecked_count > 0 && ` ${inUse.unchecked_count.toLocaleString()} could not be checked.`}
            {newerFailed && ` The newer check ${screening?.error === 'cancelled by the user' ? 'was cancelled' : `failed${screening?.error ? `: ${screening.error}` : ''}`}.`}
          </Typography>
          {flagged > 0 && <Button size="small" color="info" onClick={onReview}>Review</Button>}
          <AiAccessTooltip reason={blockedReason}>
            <Button size="small" color="info" onClick={() => setConfirmOpen(true)} disabled={!!blockedReason}>Check again</Button>
          </AiAccessTooltip>
          {dialog}
        </Box>
        {/* The same check's personal data finding (#494). */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">{personalDataOfferLine(inUse)}</Typography>
          {personalData && onReviewPersonalData && <Button size="small" color="info" onClick={onReviewPersonalData}>Review personal data</Button>}
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      {screening?.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {screening.error === 'cancelled by the user' ? 'The last check was cancelled.' : `The last check failed${screening.error ? `: ${screening.error}` : '.'}`}
        </Alert>
      )}
      <Typography variant="body1" sx={{ fontWeight: 600 }}>Check for junk answers before coding?</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        Finds empty answers, random characters, “n/a” and copied text, then you choose to keep, drop or code them. It also flags answers that contain personal data, such as names, emails and phone numbers, without changing them. Optional.
      </Typography>
      <AiAccessTooltip reason={blockedReason}>
        <Button variant="outlined" color="info" onClick={() => setConfirmOpen(true)} disabled={!!blockedReason}>
          Check for junk answers
        </Button>
      </AiAccessTooltip>
      {dialog}
    </Box>
  )
}

export default JunkCheckOffer
